import { ATS_API_URL } from '../../config';
import { httpClient } from '../../dataproviders/baseDataProvider';

export interface FraudMatchApplicant {
  applicantId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  ssn?: string;
  idDocumentNumber?: string;
}

export interface FraudMatchedApplicant {
  applicantId?: string;
  matchedFields?: string[];
  matchedValues?: Record<string, string>;
  fraudProfileIds?: number[];
  sectionTypes?: string[];
}

export interface FraudMatchedApplicationParticipant {
  applicantId?: string;
  role?: string;
  fullName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

export interface FraudMatchedApplication {
  applicationType?: 'VR' | 'ATS' | null | string;
  applicationId?: string;
  subscriberId?: string | number | null;
  createdDate?: string | number | null;
  createdOn?: string | number | null;
  createdAt?: string | number | null;
  applicationCreatedDate?: string | number | null;
  applicationCreatedOn?: string | number | null;
  appCreatedDate?: string | number | null;
  appCreatedOn?: string | number | null;
  applicantsDisplay?: string;
  matchedOn?: string[];
  sections?: string[];
  riskLevel?: 'Yes' | 'No' | string;
  matchedApplicants?: FraudMatchedApplicant[];
  participants?: FraudMatchedApplicationParticipant[];
}

export interface FraudMatchesSorting {
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FraudMatchesPaginationRequest {
  page?: number;
  pageSize?: number;
}

export interface FraudMatchesPagination {
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  sortBy?: string;
  sortOrder?: string;
}

export interface FraudMatchesQueryOptions {
  context?: {
    applicationId?: string;
    applicationType?: string;
    applicantId?: string;
    subscriberId?: string | number | null;
  };
}

export interface FraudMatchesResponse {
  riskFlag: boolean;
  matchedApplications: FraudMatchedApplication[];
  pagination?: FraudMatchesPagination | null;
}

const FRAUD_MATCHES_RESPONSE_CACHE_TTL_MS = 10 * 1000;
const inFlightFraudMatchesRequests = new Map<string, Promise<FraudMatchesResponse>>();
const fraudMatchesResponseCache = new Map<
  string,
  { expiresAt: number; response: FraudMatchesResponse }
>();

const normalizeFraudMatchesResponse = (payload: any): FraudMatchesResponse => {
  const root = payload?.data ?? payload?.records ?? payload ?? {};
  const paginationRoot = root?.pagination;

  return {
    riskFlag: Boolean(root?.riskFlag),
    matchedApplications: Array.isArray(root?.matchedApplications)
      ? root.matchedApplications
      : [],
    pagination: paginationRoot
      ? {
          page: Number(paginationRoot?.page || 1),
          pageSize: Number(paginationRoot?.pageSize || 15),
          totalRecords: Number(paginationRoot?.totalRecords || 0),
          totalPages: Number(paginationRoot?.totalPages || 0),
          hasNextPage: Boolean(paginationRoot?.hasNextPage),
          hasPreviousPage: Boolean(paginationRoot?.hasPreviousPage),
          sortBy: paginationRoot?.sortBy,
          sortOrder: paginationRoot?.sortOrder,
        }
      : null,
  };
};

const buildFraudMatchesRequestKey = (
  applicant: FraudMatchApplicant,
  options: FraudMatchesQueryOptions
) => {
  return JSON.stringify({
    applicant: {
      firstName: applicant?.firstName || '',
      lastName: applicant?.lastName || '',
      email: applicant?.email || '',
      phone: applicant?.phone || '',
      ssn: applicant?.ssn || '',
      idDocumentNumber: applicant?.idDocumentNumber || '',
    },
    context: {
      applicationId: options?.context?.applicationId || '',
      applicationType: options?.context?.applicationType || '',
      applicantId: options?.context?.applicantId || '',
      subscriberId: String(options?.context?.subscriberId ?? ''),
    },
  });
};

export const __resetFraudMatchesRequestCacheForTests = () => {
  inFlightFraudMatchesRequests.clear();
  fraudMatchesResponseCache.clear();
};

export const getFraudMatches = async (
  applicant: FraudMatchApplicant,
  options: FraudMatchesQueryOptions = {}
): Promise<FraudMatchesResponse> => {
  const requestKey = buildFraudMatchesRequestKey(applicant, options);
  const now = Date.now();
  const cached = fraudMatchesResponseCache.get(requestKey);
  if (cached && cached.expiresAt > now) {
    return cached.response;
  }
  if (cached) {
    fraudMatchesResponseCache.delete(requestKey);
  }

  const inFlightRequest = inFlightFraudMatchesRequests.get(requestKey);
  if (inFlightRequest) {
    return inFlightRequest;
  }

  const requestBody: Record<string, unknown> = {
    applicant,
  };

  if (options?.context) {
    const { applicationId, applicationType, applicantId, subscriberId } = options.context;
    requestBody.context = {
      applicationId,
      applicationType,
      applicantId,
      subscriberId,
    };
  }

  const requestPromise = (async () => {
    const { json } = await httpClient(
      `${ATS_API_URL}/applicant/riskIndicator/getFraudMatches`,
      {
        method: 'POST',
        body: JSON.stringify(requestBody),
      }
    );

    return normalizeFraudMatchesResponse(json);
  })();

  inFlightFraudMatchesRequests.set(requestKey, requestPromise);

  try {
    const response = await requestPromise;
    fraudMatchesResponseCache.set(requestKey, {
      response,
      expiresAt: Date.now() + FRAUD_MATCHES_RESPONSE_CACHE_TTL_MS,
    });
    return response;
  } finally {
    inFlightFraudMatchesRequests.delete(requestKey);
  }
};

